import { Icon } from '@buggy/shared'
import { Button as ButtonBase, notification } from 'antd'
import { isNil } from 'lodash-es'
import Image from 'next/image'
import { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useSubscribeCrawlerStatusMutation } from '@/api/workspaces-api'
import subscribeImgSrc from '@/assets/images/subscribe-image.png'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSource } from '@/types/api/search'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'

interface SubscribeCtaProps {
  isSubscribe?: boolean
  platform?: string
  kolInfo?: KolSchemaModel
}

const SubscribeCta: FunctionComponent<SubscribeCtaProps> = ({
  isSubscribe,
  platform,
  kolInfo,
}) => {
  const [subscribeCrawlerStatus, { isLoading }] =
    useSubscribeCrawlerStatusMutation()
  const { formatMessage } = useIntl()
  const searchParams = routes.kolId.useSearchParams()

  if (isNil(isSubscribe) || isNil(kolInfo)) {
    return null
  }

  const handleSubscribe = async (): Promise<void> => {
    try {
      await subscribeCrawlerStatus({
        kolUUID: kolInfo.id,
        platform,
      }).unwrap()

      ampli.subscribeKolDataUpdateNotification({
        from: 'KOL Detail',
        kolName: kolInfo.name,
        kolCountry: kolInfo.countryCode ?? undefined,
        dataSource: searchParams.data?.dataSource ?? KolSource.IqData,
      })
      notification.success({
        message: formatMessage({ id: 'kol:subscribe_cta_success' }),
        placement: 'bottomRight',
      })
    } catch (e) {
      // 訂閱一個沒有爬取任務的網紅，會得到 404
      if (e.status === 404) {
        notification.error({
          message: formatMessage({ id: 'kol:subscribe_cta_failure_404' }),
          placement: 'bottomRight',
        })
      }
    }
  }

  return (
    <Wrapper>
      <Image
        alt='subscribe cta'
        height={245}
        src={subscribeImgSrc}
        width={315}
      />
      <Content>
        <Title>{formatMessage({ id: 'kol:subscribe_cta_title' })}</Title>
        <Desc>
          {formatMessage(
            { id: 'kol:subscribe_cta_desc' },
            { kolName: kolInfo?.name },
          )}
        </Desc>
        <Button
          disabled={isSubscribe}
          icon={isSubscribe ? <Icon name='check' /> : <Icon name='bell' />}
          loading={isLoading}
          type='primary'
          onClick={handleSubscribe}
        >
          {isSubscribe
            ? formatMessage({ id: 'kol:subscribe_cta_action_text_subscribed' })
            : formatMessage({ id: 'kol:subscribe_cta_action_text' })}
        </Button>
      </Content>
    </Wrapper>
  )
}

export default SubscribeCta

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
`
const Desc = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.primary};
`
const Button = styled(ButtonBase)``

const Content = styled.div`
  ${Title} {
    margin-bottom: 16px;
  }

  ${Desc} {
    margin-bottom: 24px;
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin-top: 14px;
  padding: 40px;
  background-color: ${({ theme }): string => theme.colors.background.white};
  box-shadow: 0px 2px 12px 0px #0000001a;
`
